import { Badge, mergeClasses, tokens } from '@fluentui/react-components';

import { CompletionLabel } from 'src/models/health-goals/CompletionLabel';
import { CheckmarkCircle24Regular } from '@fluentui/react-icons';
import { Icon } from 'src/ui-models/Icon';
import { useHealthGoalHeaderStyles } from './HealthGoalHeader.styles';

export interface HealthGoalHeaderData {
  title: string
  subTitle: string
  icon: Icon
  completionLabel: CompletionLabel
  isCompleted: boolean
  isEnded: boolean
}

interface HealthGoalHeaderProps {
  healthGoalHeader: HealthGoalHeaderData
  className?: string
}

const HealthGoalHeader: React.FC<HealthGoalHeaderProps> = ({ healthGoalHeader, className }) => {
  const styles = useHealthGoalHeaderStyles();
  const { title, subTitle, completionLabel, isCompleted, isEnded, icon } = healthGoalHeader;
  const showStatus = isCompleted || isEnded;
  const labelStyle = completionLabel.isCompleted ? styles.completionLabel : styles.recommendedLabel;
  const IconComponent = icon.icon;
  const titleClasses = mergeClasses(styles.title, isEnded && styles.endedStatus);
  const containerClasses = mergeClasses(styles.titleContainer, isEnded && styles.endedStatus);

  const displayCompletionLabel = (): React.ReactNode => {
    const { text, completionTimes } = completionLabel;

    if (completionTimes) {
      const [before, after] = text.split(completionTimes);

      return (
        <>
          {before}
          <Badge appearance="filled" color="success" data-testid="badge-completion-times">
            {completionTimes}
          </Badge>
          {after}
        </>
      );
    }

    return <>{text}</>;
  };

  const getStatusText = (): string => {
    if (isCompleted) {
      return 'Completed';
    }
    if (isEnded) {
      return 'Ended';
    }
    return '';
  };

  const renderIcon = (): React.ReactNode => {
    if (isCompleted) {
      return <CheckmarkCircle24Regular className={styles.completedIcon} data-testid="completed-icon" />;
    }
    const color = isEnded ? tokens.colorNeutralForegroundDisabled : icon.color;
    return (
      <span className={styles.icon} data-testid="health-goal-icon">
        <IconComponent color={color} />
      </span>
    );
  };

  return (
    <div
      className={mergeClasses('health-goal-header-container', styles.container, className)}
      data-testid="health-goal-header"
    >
      <div className={styles.leftSection}>
        {renderIcon()}

        <div className={containerClasses} data-testid="title-container">
          <span className={titleClasses} data-testid="title">
            {title}
          </span>
          {subTitle}

          <span className={labelStyle} data-testid="completion-label">
            {displayCompletionLabel()}
          </span>
        </div>
      </div>

      <div className={styles.rightSection}>
        {showStatus && (
          <span className={styles.statusText} data-testid="status-text">
            {getStatusText()}
          </span>
        )}
      </div>
    </div>
  );
};

export default HealthGoalHeader;
